import store from '../store/store';
import { reportClientError } from '../slices/clientErrors/thunks/report-client-error.thunk';
import { ClientErrorSource } from '../models/ClientErrors/ClientErrorSource.enum';
import { PostClientErrorReport } from '../models/ClientErrors/PostClientErrorReport';

const CONSOLE_RING_BUFFER_SIZE = 20;
const CONSOLE_ENTRY_MAX_LENGTH = 500;
const MAX_REPORTS_PER_SESSION = 10;

let consoleRingBuffer: string[] = [];
let reportedSignatures = new Set<string>();
let reportedCount = 0;
let isInitialized = false;
let originalConsoleError: typeof console.error = console.error;
let originalConsoleWarn: typeof console.warn = console.warn;

const stringifyConsoleArg = (arg: unknown): string => {
    if (typeof arg === 'string') {
        return arg;
    }
    try {
        return JSON.stringify(arg);
    } catch {
        return String(arg);
    }
};

const stringifyConsoleArgs = (args: unknown[]): string => {
    const joined = args.map(stringifyConsoleArg).join(' ');
    return joined.length > CONSOLE_ENTRY_MAX_LENGTH
        ? joined.slice(0, CONSOLE_ENTRY_MAX_LENGTH)
        : joined;
};

const pushConsoleEntry = (entry: string): void => {
    consoleRingBuffer.push(entry);
    if (consoleRingBuffer.length > CONSOLE_RING_BUFFER_SIZE) {
        consoleRingBuffer.shift();
    }
};

export const getConsoleRingBuffer = (): string[] => [...consoleRingBuffer];

export const installConsoleRingBuffer = (): void => {
    originalConsoleError = console.error.bind(console);
    originalConsoleWarn = console.warn.bind(console);

    console.error = (...args: unknown[]) => {
        pushConsoleEntry(stringifyConsoleArgs(args));
        originalConsoleError(...args);
    };
    console.warn = (...args: unknown[]) => {
        pushConsoleEntry(stringifyConsoleArgs(args));
        originalConsoleWarn(...args);
    };
};

export const restoreConsoleWrappers = (): void => {
    console.error = originalConsoleError;
    console.warn = originalConsoleWarn;
};

export const resetErrorReportingStateForTests = (): void => {
    consoleRingBuffer = [];
    reportedSignatures = new Set<string>();
    reportedCount = 0;
    isInitialized = false;
};

const buildErrorSignature = (message: string, source: ClientErrorSource): string =>
    `${source}:${message}`;

const getAppVersion = (): string => process.env.NODE_ENV;

const buildContext = (extraContext?: Record<string, unknown>): string | null => {
    try {
        return JSON.stringify({
            screen: `${window.screen?.width ?? 0}x${window.screen?.height ?? 0}`,
            viewport: `${window.innerWidth}x${window.innerHeight}`,
            ...extraContext,
        });
    } catch {
        return null;
    }
};

const buildFailedApiCallContext = (reason: any): Record<string, unknown> | undefined => {
    const url: unknown = reason?.config?.url;
    if (typeof url !== 'string') {
        return undefined;
    }
    return {
        apiCall: {
            method: reason.config?.method ?? null,
            url: url.split('?')[0],
            status: reason.response?.status ?? null,
        },
    };
};

const submitReport = (payload: PostClientErrorReport): void => {
    try {
        const signature = buildErrorSignature(payload.message, payload.source);
        if (reportedSignatures.has(signature)) {
            return;
        }
        if (reportedCount >= MAX_REPORTS_PER_SESSION) {
            return;
        }
        reportedSignatures.add(signature);
        reportedCount += 1;
        store.dispatch(reportClientError(payload) as any);
    } catch {
        // Reporting failures must never throw and must never themselves be reported.
    }
};

export const reportError = (
    message: string,
    source: ClientErrorSource,
    stack: string | null = null,
    componentStack: string | null = null,
    extraContext?: Record<string, unknown>
): void => {
    submitReport({
        message: message || 'Unknown error',
        stack,
        componentStack,
        source,
        route: window.location.pathname,
        userAgent: window.navigator?.userAgent ?? null,
        appVersion: getAppVersion(),
        consoleEntries: getConsoleRingBuffer(),
        context: buildContext(extraContext),
        occurredAt: new Date().toISOString(),
    });
};

const handleWindowError = (event: ErrorEvent): void => {
    reportError(
        event?.message || 'Unknown window error',
        ClientErrorSource.WindowOnError,
        event?.error?.stack ?? null
    );
};

const handleUnhandledRejection = (event: PromiseRejectionEvent): void => {
    const reason: any = event?.reason;
    const message = reason?.message ?? String(reason);
    const stack = reason?.stack ?? null;
    reportError(message, ClientErrorSource.UnhandledRejection, stack, null, buildFailedApiCallContext(reason));
};

export const initGlobalErrorReporting = (): void => {
    if (isInitialized) {
        return;
    }
    isInitialized = true;

    installConsoleRingBuffer();
    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
};
